import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePreviousValue } from 'beautiful-react-hooks';
import { DateTime } from 'luxon';

import { showSnackbar } from 'helpers/snackbar';

import { app, media } from 'context';
import { APP_BANNER_HEIGHT, Animate, Layout, Text } from 'ds';
import usePageVisibility from 'hooks/usePageVisibility';
import { actions } from 'store/AIAssistant';
import { selectAIAssistantConversationById, selectPendingConversationMessage } from 'store/AIAssistant/selectors';
import { selectUIState } from 'store/UI/selectors';
import { selectUser } from 'store/User/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';
import { createOrUpdateSupportTicketView } from 'ux/Customer/Support/requests';

import AIAssistantMessage from './AIAssistantMessage';
import AIAssistantThinkingDisplay from './AIAssistantThinkingDisplay';
import AIAssistantUserMessage from './AIAssistantUserMessage';
import { addFeedbackToMessage } from './requests';
import { getGreetingVariation } from './utils';

interface AIAssistantConversationProps {
  conversationId: number;
  onThreadsRendered?: () => void;
  /** Scroll area fills parent (e.g. side drawer) instead of viewport-based height. */
  layout?: 'page' | 'embedded';
}

const THREAD_ID_TO_GREETING_VARIATION = new Map<number, string>();

const AIAssistantConversation: React.FC<AIAssistantConversationProps> = ({
  conversationId,
  onThreadsRendered,
  layout = 'page'
}) => {
  const user = useAppSelector(selectUser);
  const tenancyId = useAppSelector(selectTenancyId);
  const parsedConversationId = conversationId || -1;
  const conversation = useAppSelector(selectAIAssistantConversationById(parsedConversationId));
  const { appBannerType } = useAppSelector(selectUIState);
  const { isMobile, sm } = useContext(media);
  const { appPaddingX, bottomNavigationHeight } = useContext(app);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const conversationInputContainerMarginTop = 36;
  const conversationInputContainerMarginBottom = 36;
  const conversationInputHeight = 56;
  const conversationInputContainerHeight =
    conversationInputContainerMarginTop + conversationInputContainerMarginBottom + conversationInputHeight;
  const topPageOffset = 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const messages = (conversation?.ai_assistant_messages || []).filter((message, index, arr) => {
    if (index === arr.length - 1) {
      return message.role !== 'user';
    }
    return true;
  });
  const [messageIdsCreatedSincePageLoad, setMessageIdsCreatedSincePageLoad] = useState<number[]>([]);
  const previousMessageCount = usePreviousValue(messages?.length || 0);

  let greetingVariation = THREAD_ID_TO_GREETING_VARIATION.get(parsedConversationId);
  if (!greetingVariation) {
    greetingVariation = getGreetingVariation(user?.firstname || '');
    THREAD_ID_TO_GREETING_VARIATION.set(parsedConversationId, greetingVariation);
  }

  const pendingMessage = useAppSelector(selectPendingConversationMessage(parsedConversationId));
  const lastMessage = conversation?.ai_assistant_messages.slice(-1)[0];
  const [hasScrolled, setHasScrolled] = useState(false);

  const supportTicket =
    lastMessage?.role === 'metadata' && lastMessage.metadata.type === 'support_ticket'
      ? lastMessage.metadata.support_ticket
      : null;

  useEffect(() => {
    if (previousMessageCount > 0 && messages?.length && messages.length > previousMessageCount) {
      const newMessages = messages.slice(previousMessageCount);
      newMessages.forEach(message => {
        if (messageIdsCreatedSincePageLoad.includes(message.id)) return;
        setMessageIdsCreatedSincePageLoad(prev => [...prev, message.id]);
      });
    }
  }, [messages?.length, previousMessageCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSupportTicketView = async () => {
    if (supportTicket?.id) {
      const { data } = await createOrUpdateSupportTicketView({ supportTicketId: supportTicket.id });

      dispatch(actions.updateSupportTicket(data));
    }
  };

  usePageVisibility(isVisible => {
    if (isVisible) {
      handleSupportTicketView();
    }
  });

  useEffect(() => {
    if (!supportTicket?.id) return;

    handleSupportTicketView();
  }, [supportTicket?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (conversationId === -1) {
        THREAD_ID_TO_GREETING_VARIATION.delete(conversationId);
      }
    };
  }, [conversationId]);

  useEffect(() => {
    if (!messages?.length) return;

    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) return;

    if (!hasScrolled) {
      scrollContainerRef.current?.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'instant'
      });
      setHasScrolled(true);
      return;
    }

    setTimeout(() => {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'smooth'
      });
    }, 300);
  }, [messages?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const dispatch = useAppDispatch();
  const messagesOrdered = useMemo(
    () =>
      messages
        .map(message => ({
          ...message,
          conversation: conversation
        }))
        .sort((a, b) => DateTime.fromISO(a.created_at).toMillis() - DateTime.fromISO(b.created_at).toMillis()) || [],
    [messages, conversation]
  );

  useEffect(() => {
    if (messagesOrdered.length > 0 && onThreadsRendered) {
      onThreadsRendered();
    }
  }, [messagesOrdered, onThreadsRendered]);

  if (!user) return null;

  const handleAddFeedback = async (messageId: number, liked: boolean) => {
    if (!tenancyId) return;
    const response = await addFeedbackToMessage(tenancyId, parsedConversationId, messageId, liked);
    if (response.status === 200) {
      showSnackbar({
        message: 'Thanks for the feedback!',
        type: 'light',
        position: 'top'
      });
      dispatch(actions.updateMessageFeedback({ messageId, conversationId: parsedConversationId }));
    } else {
      showSnackbar({
        message: 'Failed to add feedback',
        negative: true,
        position: 'top'
      });
    }
  };

  const isEmbedded = layout === 'embedded';

  return (
    <Layout
      id="ai-assistant-conversation-container"
      className="AIAssistantConversation-tenant"
      __ref={scrollContainerRef}
      width="100%"
      minWidth={0}
      {...(isEmbedded
        ? { flexGrow: 1, minHeight: 0 }
        : isMobile
          ? { paddingBottom: bottomNavigationHeight + 96 }
          : {
              height: `calc(100vh - ${
                conversationInputContainerHeight + (topPageOffset + (appBannerType ? APP_BANNER_HEIGHT * 2 : 0))
              }px)`
            })}
      flex
      justify="center"
      overflowY="auto"
      overflowX="hidden"
    >
      <Layout
        flex
        direction="column"
        justify="flex-start"
        width="100%"
        minWidth={0}
        {...(isEmbedded
          ? {
              paddingX: appPaddingX,
              paddingTop: 16,
              paddingBottom: 16
            }
          : isMobile
            ? {
                paddingX: appPaddingX,
                paddingTop: 60,
                minHeight: window.innerHeight
              }
            : sm
              ? { paddingX: appPaddingX }
              : { maxWidth: 768 })}
      >
        {!!conversation?.created_at && (
          <Animate
            animation="fade-up"
            visible={true}
            duration={300}
            delay={20}
            justify="center"
            keyframes={[
              {
                transform: 'translateY(20px)'
              },
              {
                transform: 'translateY(0)'
              }
            ]}
          >
            <Layout justify="center" align="center" paddingY={12}>
              <Layout borderRadius={1000} color="gray-25" paddingX={12} paddingY={2} flexShrink={1}>
                <Text size="body-sm">{DateTime.fromISO(conversation?.created_at).toFormat('MMM d, h:mm a')}</Text>
              </Layout>
            </Layout>
          </Animate>
        )}
        {messagesOrdered.map((message, index) => {
          const wasJustCreated = DateTime.fromISO(message.created_at).diffNow().toMillis() > -2_000;

          // Check if we need to show a timestamp (>= 1 day from previous message)
          // Show timestamp immediately on first message that's been a while, not waiting for response
          const shouldShowTimestamp = (() => {
            if (index === 0) {
              // For the first message, check if it's been a while since conversation creation
              if (conversation?.created_at) {
                const messageTime = DateTime.fromISO(message.created_at);
                const conversationTime = DateTime.fromISO(conversation.created_at);
                const daysDiff = messageTime.diff(conversationTime, 'days').days;
                return daysDiff >= 1;
              }
              return false;
            }

            // For subsequent messages, check if current message is significantly after previous
            const currentMessageTime = DateTime.fromISO(message.created_at);
            const previousMessageTime = DateTime.fromISO(messagesOrdered[index - 1].created_at);
            const daysDiff = currentMessageTime.diff(previousMessageTime, 'days').days;
            return daysDiff >= 1;
          })();

          return (
            <React.Fragment key={message.id}>
              {shouldShowTimestamp && (
                <Layout justify="center">
                  <Layout justify="center" align="center" paddingY={12}>
                    <Layout borderRadius={1000} color="gray-25" paddingX={12} paddingY={2} flexShrink={1}>
                      <Text size="body-sm">{DateTime.fromISO(message.created_at).toFormat('MMM d, h:mm a')}</Text>
                    </Layout>
                  </Layout>
                </Layout>
              )}
              <AIAssistantMessage
                message={message}
                handleAddFeedback={handleAddFeedback}
                conversationId={conversationId}
                createdSincePageLoad={messageIdsCreatedSincePageLoad.includes(message.id) || wasJustCreated}
              />
            </React.Fragment>
          );
        })}
        {!!pendingMessage &&
          (() => {
            // Check if we need to show a timestamp above the pending message
            const shouldShowTimestampForPending = (() => {
              if (messagesOrdered.length === 0) {
                // If no previous messages, check if it's been a while since conversation creation
                if (conversation?.created_at) {
                  const now = DateTime.now();
                  const conversationTime = DateTime.fromISO(conversation.created_at);
                  const daysDiff = now.diff(conversationTime, 'days').days;
                  return daysDiff >= 1;
                }
                return false;
              }

              // Check if current time is significantly after the last message
              const now = DateTime.now();
              const lastMessageTime = DateTime.fromISO(messagesOrdered[messagesOrdered.length - 1].created_at);
              const daysDiff = now.diff(lastMessageTime, 'days').days;
              return daysDiff >= 1;
            })();

            return (
              <>
                {shouldShowTimestampForPending && (
                  <Layout justify="center">
                    <Layout justify="center" align="center" paddingY={12}>
                      <Layout borderRadius={1000} color="gray-25" paddingX={12} paddingY={2} flexShrink={1}>
                        <Text size="body-sm">{DateTime.now().toFormat('MMM d, h:mm a')}</Text>
                      </Layout>
                    </Layout>
                  </Layout>
                )}
                <Animate
                  marginBottom={24}
                  animation="fade-up"
                  duration={300}
                  marginLeft={24}
                  marginRight={isMobile ? -appPaddingX + 24 : undefined}
                  delay={0}
                  justify="flex-end"
                  visible
                >
                  <AIAssistantUserMessage>{pendingMessage.input}</AIAssistantUserMessage>
                </Animate>
                <Animate marginTop={24} delay={300} duration={300} visible>
                  <AIAssistantThinkingDisplay />
                </Animate>
              </>
            );
          })()}
      </Layout>
    </Layout>
  );
};

export default AIAssistantConversation;
